import { useEffect, useState } from 'react';

/**
 * Qué mide el teléfono, en números, para que no haya que adivinarlo.
 *
 * El 29-oct-2026 Daniel fotografió un hueco de 62 pt de fondo claro DEBAJO de
 * la barra de abajo, en todas las pantallas con shell. De las fotos se dedujo
 * que el shell termina en 894 de una pantalla de 956 —894 = 956 − 62, que es
 * justo el recorte de ARRIBA— y que la barra de abajo mide 50 en vez de 82, o
 * sea que `env(safe-area-inset-bottom)` está reportando 0 en ese aparato.
 *
 * Eso no se puede reproducir en Chromium: headless calcula `dvh` bien y la app
 * no corre en modo standalone, que es donde iOS cambia las reglas. Así que la
 * medición tiene que salir del aparato. Esta pantalla la hace en un toque.
 *
 * `env(...)` no se puede leer con `getComputedStyle` —devuelve la expresión sin
 * resolver—, así que se mide con cuatro sondas: divs cuyo alto/ancho ES el
 * recorte. Lo que mide el navegador es lo que reporta.
 */
export function DiagnosticoPage() {
  const [d, setD] = useState<Record<string, string>>({});

  useEffect(() => {
    const sonda = (lado: string) => {
      const e = document.createElement('div');
      e.style.cssText = `position:fixed;left:-9999px;height:env(safe-area-inset-${lado},0px)`;
      document.body.appendChild(e);
      const v = e.getBoundingClientRect().height;
      e.remove();
      return v;
    };
    const raiz = document.getElementById('root')?.getBoundingClientRect();
    const vv = window.visualViewport;
    setD({
      'pantalla (screen)': `${screen.width} × ${screen.height}`,
      'ventana (innerWidth/Height)': `${innerWidth} × ${innerHeight}`,
      'documento (clientWidth/Height)': `${document.documentElement.clientWidth} × ${document.documentElement.clientHeight}`,
      'visualViewport': vv ? `${Math.round(vv.width)} × ${Math.round(vv.height)} · offsetTop ${Math.round(vv.offsetTop)}` : '—',
      '#root (alto · top · bottom)': raiz ? `${Math.round(raiz.height)} · ${Math.round(raiz.top)} · ${Math.round(raiz.bottom)}` : '—',
      'recorte arriba': `${sonda('top')} px`,
      'recorte abajo': `${sonda('bottom')} px`,
      // Lo que resuelve `lib/lienzoCorto.ts`. Si el lienzo vino corto, «sobra»
      // trae esos píxeles y la reserva que paga la barra baja a 0: es la única
      // forma de leer desde el teléfono si la compensación disparó.
      'sobra del lienzo': document.documentElement.style.getPropertyValue('--sobra-lienzo') || '(sin medir)',
      'reserva que paga la barra': `${medirUnidad('var(--franja-abajo)')} px`,
      '100dvh': `${medirUnidad('100dvh')} px`,
      '100svh': `${medirUnidad('100svh')} px`,
      '100lvh': `${medirUnidad('100lvh')} px`,
      '100vh': `${medirUnidad('100vh')} px`,
      'modo app (standalone)': String((navigator as unknown as { standalone?: boolean }).standalone ?? matchMedia('(display-mode: standalone)').matches),
      'píxeles por punto': String(devicePixelRatio),
    });
  }, []);

  // Lo que SOBRA abajo: la pantalla menos donde termina el shell. Es el número
  // que Daniel ve como banda de color claro.
  const raiz = document.getElementById('root')?.getBoundingClientRect();
  const sobra = raiz ? Math.round(screen.height - raiz.bottom) : 0;

  return (
    <div className="h-full overflow-y-auto bg-white p-4">
      <h1 className="mb-1 text-xl font-bold">Diagnóstico de pantalla</h1>
      <p className="mb-4 text-sm text-stone-500">
        Sacá una foto de esta pantalla y mandámela. Con esto se cierra el caso
        del espacio vacío de abajo sin adivinar.
      </p>

      <div className={`mb-4 rounded-lg p-3 text-center ${sobra > 2 ? 'bg-red-50' : 'bg-green-50'}`}>
        <div className="text-sm uppercase tracking-wide text-stone-600">sobra abajo</div>
        <div className={`text-4xl font-bold ${sobra > 2 ? 'text-red-600' : 'text-green-700'}`}>{sobra} pt</div>
      </div>

      <table className="w-full text-sm">
        <tbody>
          {Object.entries(d).map(([k, v]) => (
            <tr key={k} className="border-b border-stone-100">
              <td className="py-1.5 pr-2 text-stone-500">{k}</td>
              <td className="py-1.5 text-right font-mono font-semibold">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Cuánto mide de verdad una unidad de viewport en ESTE aparato. */
function medirUnidad(u: string) {
  const e = document.createElement('div');
  e.style.cssText = `position:fixed;left:-9999px;top:0;height:${u}`;
  document.body.appendChild(e);
  const v = Math.round(e.getBoundingClientRect().height);
  e.remove();
  return v;
}
